import {FC, useMemo} from 'react'
import clsx from 'clsx'
import {useThemeMode} from '../../../../../../../../../_metronic/partials'
import './TaskComments.css'

type CommentContentProps = {
  content: string
  mentions?: number[]
  attachments?: string[]
  isPlainText?: boolean
}

export const CommentContent: FC<CommentContentProps> = ({
  content,
  mentions = [],
  attachments = [],
  isPlainText = false,
}) => {
  const {mode} = useThemeMode()

  // Process HTML content and render mentions
  const processedContent = useMemo(() => {
    if (!content) return ''

    // Convert mention tags to styled mentions
    let processed = content.replace(
      /<mention[^>]*data-user-id=["'](\d+)["'][^>]*data-user-name=["']([^"']+)["'][^>]*>([^<]*)<\/mention>/gi,
      (match, userId, userName, displayText) => {
        const text = displayText || `@${userName}`
        return `<span class="mention-badge" data-user-id="${userId}" data-user-name="${userName}">${text}</span>`
      }
    )

    // Ensure images have proper attributes and fix URLs
    processed = processed.replace(
      /<img([^>]*)>/gi,
      (match, attrs) => {
        let newAttrs = attrs
        
        // Fix image src if it's a relative path or needs URL correction
        if (newAttrs.includes('src=')) {
          const srcMatch = newAttrs.match(/src=["']([^"']+)["']/i)
          if (srcMatch) {
            let src = srcMatch[1]
            const apiBase = process.env.REACT_APP_THEME_API_URL || ''

            // Always use API base for ckeditor-file proxy so image loads from the same origin as API calls
            if (src.includes('/api/ckeditor-file')) {
              try {
                const u = new URL(src)
                // Use & in query so the request has separate params (key, expires, signature); &amp; would send all in key
                const query = (u.search || '').replace(/&amp;/gi, '&')
                const pathAndQuery = u.pathname + query
                src = apiBase ? `${apiBase.replace(/\/$/, '')}${pathAndQuery}` : src
              } catch {
                // keep src unchanged if URL parsing fails
              }
              newAttrs = newAttrs.replace(/src=["'][^"']+["']/i, `src="${src}"`)
            }
            // If src is a path like "tasks/filename.jpg" (from attachments), convert to full URL
            else if (src.startsWith('tasks/') && !src.startsWith('http')) {
              src = `${apiBase}/storage/${src}`
              newAttrs = newAttrs.replace(/src=["'][^"']+["']/i, `src="${src}"`)
            }
            // If src is relative and doesn't start with http/https or /storage
            else if (!src.startsWith('http') && !src.startsWith('/storage') && !src.startsWith('storage/') && !src.startsWith('data:')) {
              src = `${apiBase}/storage/${src}`
              newAttrs = newAttrs.replace(/src=["'][^"']+["']/i, `src="${src}"`)
            }
          }
        }
        
        if (!newAttrs.includes('loading=')) {
          return `<img${newAttrs} loading="lazy">`
        }
        return `<img${newAttrs}>`
      }
    )

    // Ensure links open in new tab
    processed = processed.replace(
      /<a([^>]*)>/gi,
      (match, attrs) => {
        if (!attrs.includes('target=')) {
          return `<a${attrs} target="_blank" rel="noopener noreferrer">`
        }
        return match
      }
    )

    return processed
  }, [content])

  // Render plain text comments (backward compatibility)
  if (isPlainText || !content) {
    return <span>{content || ''}</span>
  }

  return (
    <div
      className={clsx('comment-content', mode === 'dark' && 'comment-content-dark')}
      dangerouslySetInnerHTML={{__html: processedContent}}
    />
  )
}
